/**
 * 呼入排队信息 Hook
 * @author easonmma
 */
import { useCallback, useEffect, useState } from 'react'
import { getIncomingQueueCount } from '@services/t3c'
import { QueueInfo } from '@types'

export interface UseQueueInfoOptions {
  /** 是否启用自动刷新 */
  autoRefresh?: boolean
  /** 刷新间隔（毫秒），默认30秒 */
  refreshInterval?: number
  /** 是否启用（只有在使用T3C时才启用） */
  enabled?: boolean
}

export function useQueueInfo (options: UseQueueInfoOptions = {}) {
  const {
    autoRefresh = true,
    refreshInterval = 5000,
    enabled = true,
  } = options

  const [queueInfo, setQueueInfo] = useState<QueueInfo>({ skillGroups: [] })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // 获取排队人数
  const fetchQueueInfo = useCallback(async () => {
    if (!enabled) return

    setLoading(true)
    setError(null)

    try {
      const [res, err] = await getIncomingQueueCount()
      if (err) {
        console.error('获取呼入排队人数失败:', err)
        setError(err.message || '获取失败')
        // 出错时保持显示空数组
        // setQueueInfo({ skillGroups: [] })
      } else {
        setQueueInfo(res || { skillGroups: [] })
      }
    } catch (error) {
      console.error('获取呼入排队人数异常:', error)
      setError('网络异常')
      setQueueInfo({ skillGroups: [] })
    } finally {
      setLoading(false)
    }
  }, [enabled])

  // 初始化获取数据
  useEffect(() => {
    if (enabled) {
      fetchQueueInfo()
    }
  }, [enabled, fetchQueueInfo])

  // 自动刷新
  useEffect(() => {
    if (!autoRefresh || !enabled) return

    const timer = setInterval(() => {
      fetchQueueInfo()
    }, refreshInterval)

    return () => clearInterval(timer)
  }, [autoRefresh, refreshInterval, enabled, fetchQueueInfo])

  return {
    queueInfo,
    loading,
    error,
    refresh: fetchQueueInfo,
  }
}
